import { useSuspenseQuery } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import sortBy from 'lodash/sortBy';
import React, { Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ErrorAlert } from '@eluve/blocks';
import {
  Badge,
  Icon,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  ToggleGroup,
  ToggleGroupItem,
} from '@eluve/components';
import { LocationsTree } from '@eluve/frontend-feature-location-hierarchy';
import { graphql } from '@eluve/graphql.tada';

const getLocationsTableDataQuery = graphql(`
  query getLocationsTableData($tenantId: uuid!) {
    tenantsByPk(id: $tenantId) {
      __typename
      id
      layers {
        __typename
        id
        name
        isRoot
      }
      locations {
        __typename
        id
        name
        sourceName
        externalId
        isRoot
        updatedAt
        externalEhr {
          __typename
          id
          vendor
        }
        appointmentsAggregate {
          aggregate {
            count
          }
        }
        location_users {
          __typename
          locationId
          userId
        }
      }
    }
  }
`);

const RenamedBadge: React.FC = () => (
  <Badge variant="warningSubtle" uppercase>
    renamed
  </Badge>
);

const LocationsTableView: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(getLocationsTableDataQuery, {
    variables: { tenantId },
  });

  const allLocations = data.tenantsByPk?.locations ?? [];
  const rootLayerName =
    data.tenantsByPk?.layers.find((l) => l.isRoot)?.name ?? 'Root';
  const rows = sortBy(
    allLocations,
    (l) => !l.isRoot,
    (l) => l.name.toLowerCase(),
  );

  if (rows.length === 0) {
    return (
      <Text color="secondary">
        No locations yet — switch to the Tree view to add one.
      </Text>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border-primary">
      <Table>
        <TableHeader>
          <TableRow className="bg-bg-secondary">
            <TableHead className="px-6">Location</TableHead>
            <TableHead>Source Org ID</TableHead>
            <TableHead className="text-right">Users</TableHead>
            <TableHead className="text-right">Appts</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((location) => {
            const isRenamed =
              location.sourceName != null &&
              location.name !== location.sourceName;
            const locationPath = `/organizations/${tenantId}/admin/locations/${location.id}`;

            return (
              <TableRow
                key={location.id}
                className="cursor-pointer"
                onClick={() => navigate(locationPath)}
              >
                <TableCell className="px-6 py-4">
                  <Stack gap={0} align="start">
                    <Stack
                      direction="horizontal"
                      gap={2}
                      align="center"
                      fullWidth={false}
                    >
                      <Link
                        className="font-semibold hover:underline"
                        to={locationPath}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {location.name}
                      </Link>
                      {location.isRoot && (
                        <Badge variant="secondary" uppercase>
                          {rootLayerName}
                        </Badge>
                      )}
                      {isRenamed && <RenamedBadge />}
                    </Stack>
                    {location.sourceName != null && (
                      <Text size="sm" color="secondary">
                        {location.sourceName}
                      </Text>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Text size="sm" color="secondary" className="font-mono">
                    {location.externalId || '—'}
                  </Text>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {location.location_users.length}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {(
                    location.appointmentsAggregate.aggregate?.count ?? 0
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="w-10">
                  <Link
                    to={locationPath}
                    aria-label={`Open ${location.name}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Icon
                      name="ChevronRight"
                      size="xs"
                      className="text-text-secondary"
                    />
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export type LocationsViewMode = 'table' | 'tree';

export const LocationsViewToggle: React.FC<{
  view: LocationsViewMode;
  onViewChange: (view: LocationsViewMode) => void;
}> = ({ view, onViewChange }) => (
  <ToggleGroup
    className="rounded-lg bg-bg-tertiary p-1"
    spacing={1}
    type="single"
    value={view}
    onValueChange={(value) => {
      if (value) {
        onViewChange(value as LocationsViewMode);
      }
    }}
  >
    <ToggleGroupItem
      className="rounded-md px-4 data-[state=on]:bg-bg-primary data-[state=on]:shadow-sm"
      value="table"
    >
      Table
    </ToggleGroupItem>
    <ToggleGroupItem
      className="rounded-md px-4 data-[state=on]:bg-bg-primary data-[state=on]:shadow-sm"
      value="tree"
    >
      Tree
    </ToggleGroupItem>
  </ToggleGroup>
);

export const LocationsView: React.FC<{
  tenantId: string;
  view: LocationsViewMode;
}> = ({ tenantId, view }) => {
  return (
    <Stack gap={4} align="stretch">
      <ErrorBoundary
        fallback={({ resetError }) => (
          <ErrorAlert message="Failed to load locations" reset={resetError} />
        )}
      >
        <Suspense fallback={<Skeleton />}>
          {view === 'table' ? (
            <LocationsTableView tenantId={tenantId} />
          ) : (
            <LocationsTree tenantId={tenantId} />
          )}
        </Suspense>
      </ErrorBoundary>
    </Stack>
  );
};
